import { Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';

import { IAddressForm, Service } from '../addressBook.const';

interface ServiceCheckboxGroupProps {
    services: Service[];
    name: 'pickupAdditionalService' | 'deliveryAdditionalService';
    control: Control<IAddressForm>;
    title: string;
    showInfoIcon?: boolean;
    onInfoClick?: () => void;
    onEditService?: (service: Service) => void;
    // Return true to mark as handled and skip default toggle behavior
    onServiceToggle?: (service: Service, checked: boolean) => boolean;
}

const ServiceCheckboxGroup: React.FC<ServiceCheckboxGroupProps> = ({ services, name, control, title, showInfoIcon = false, onInfoClick, onEditService, onServiceToggle }) => {
    const { t } = useTranslation();

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const handleEditService = (service: Service) => () => {
        if (onEditService) {
            onEditService(service);
        }
    };

    return (
        <div>
            <div className="flex items-center gap-x-1.5 mb-1.5">
                <h6 className="text-textSecondary text-xs font-medium">{title}</h6>
                {showInfoIcon && onInfoClick && (
                    <div className="cursor-pointer" onClick={onInfoClick}>
                        <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                    </div>
                )}
            </div>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <div className="rounded-lg border border-utilityGray200 bg-white shadow-sm p-3">
                        <div className="flex flex-wrap -m-1">
                            {services.map((service: any) => (
                                <div className="flex p-1 w-1/3" key={`service_${service.id}`}>
                                    <CheckBox
                                        id={`${name}_${service.id}`}
                                        onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const handled = onServiceToggle?.(service, e.target.checked);

                                            if (!handled) {
                                                const updatedServices = e.target.checked ? [...(field.value || []), service.id] : (field.value || []).filter((id) => id !== service.id);
                                                field.onChange(updatedServices);
                                            }
                                        }}
                                        name={service.slug}
                                        value={service.id}
                                        checked={(field.value || []).includes(service.id)}
                                        parentClassName="mt-px"
                                        disabled={service.isOnlyForFTL}
                                    />
                                    <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-1.5 flex-1">
                                        {['pickup_appointment_required', 'after_hours_pickup', 'delivery_appointment_required'].includes(service.slug) ? (
                                            <TooltipCmp message={service.name}>
                                                <label
                                                    htmlFor={`${name}_${service.id}`}
                                                    className={`leading-4 text-textSecondary text-xs font-medium truncate ${service.isOnlyForFTL ? 'line-through' : 'cursor-pointer'}`}
                                                >
                                                    {service.slug === 'pickup_appointment_required'
                                                        ? t('QuotingHub.Common.Pickup_Appointment')
                                                        : service.slug === 'delivery_appointment_required'
                                                        ? t('QuotingHub.Common.Delivery_Appointments')
                                                        : t('QuotingHub.Common.After_Hours_Pickup')}
                                                </label>
                                            </TooltipCmp>
                                        ) : (
                                            <label
                                                htmlFor={`${name}_${service.id}`}
                                                className={`leading-4 text-textSecondary text-xs font-medium truncate ${service.isOnlyForFTL ? 'line-through' : 'cursor-pointer'}`}
                                            >
                                                {service.name}
                                            </label>
                                        )}
                                        {(field.value || []).includes(service.id) && EDITABLE_SERVICES_SLUGS.includes(service.slug || '') && onEditService ? (
                                            <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(service)} />
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

export default ServiceCheckboxGroup;
